import {
  Stack,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
} from '@mui/material'
import { 
  Biotech,
  Science,
  Psychology,
  Calculate,
  Palette,
  Edit,
} from '@mui/icons-material'

const GeneticsTab = ({ 
  entry,
  setEditMode,
  onTestBreeding,
  breedingResults,
  setBreedingResults,
  chickenGenes,
  getGeneticProfile,
  getELocusPhenotype,
  getPhenotype
}) => {
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Biotech color="primary" />
          Genetics Profile
        </Typography>
        <Button
          variant="contained"
          startIcon={<Calculate />}
          onClick={onTestBreeding}
          size="small"
        >
          Test Breeding
        </Button>
      </Box>

      {/* Current Genetics Profile */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Science color="primary" />
          Genetic Makeup
        </Typography>
        
        {entry?.genetics ? (
          <Grid container spacing={2}>
            {Object.entries(chickenGenes).map(([gene, info]) => {
              const birdGenetics = getGeneticProfile(entry)
              
              if (gene === 'E') {
                const alleles = birdGenetics.E || ['?', '?']
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={gene}>
                    <Box sx={{ 
                      p: 2, 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: alleles[0] !== '?' ? 'success.main' : 'grey.100',
                      color: alleles[0] !== '?' ? 'success.contrastText' : 'text.secondary',
                      opacity: alleles[0] !== '?' ? 1 : 0.7
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {gene} - {info.name}
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                        {alleles[0]}/{alleles[1]}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {alleles[0] !== '?' ? getELocusPhenotype(alleles) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Grid>
                )
              } else {
                const alleles = birdGenetics[gene] || ['?', '?']
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={gene}>
                    <Box sx={{ 
                      p: 2, 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: alleles[0] !== '?' ? 'success.main' : 'grey.100',
                      color: alleles[0] !== '?' ? 'success.contrastText' : 'text.secondary',
                      opacity: alleles[0] !== '?' ? 1 : 0.7
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {gene} - {info.name}
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                        {alleles[0]}{alleles[1]}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {alleles[0] !== '?' ? getPhenotype(gene, alleles) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Grid>
                )
              }
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Psychology sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Genetics Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add genetic information to enable breeding calculations and lineage tracking.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEditMode(true)}
            >
              Add Genetics
            </Button>
          </Box>
        )}
      </Paper>

      {/* Color Genetics Guide */}
      <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette color="primary" />
          Genetics Guide
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(chickenGenes).slice(0, 6).map(([gene, info]) => (
            <Grid size={{ xs: 12, sm: 6 }} key={gene}>
              <Box sx={{ p: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>{gene}</strong> - {info.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {info.description}
                </Typography>
                {gene === 'E' ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                      Dominance Order (highest to lowest):
                    </Typography>
                    {Object.entries(info.alleles)
                      .sort(([,a], [,b]) => b.dominance - a.dominance)
                      .map(([allele, details]) => (
                        <Typography key={allele} variant="caption" display="block">
                          <strong>{allele}</strong> - {details.name}
                        </Typography>
                      ))
                    }
                  </Box>
                ) : (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Dominant: <strong>{info.dominant}</strong> | Recessive: <strong>{info.recessive}</strong>
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Breeding Results Display */}
      {breedingResults && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calculate color="primary" />
            Breeding Results: {entry?.name} × {breedingResults.mate.name}
          </Typography>
          
          <Stack spacing={2}>
            {breedingResults.genetics.map((result, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {result.gene} - {result.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {entry?.name}: {result.parent1.join('')} × {breedingResults.mate.name}: {result.parent2.join('')}
                </Typography>
                
                <Grid container spacing={1}>
                  {result.outcomes.map((outcome, outIndex) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={outIndex}>
                      <Box sx={{ 
                        p: 1, 
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: outcome.percentage > 50 ? 'success.light' : 'grey.50'
                      }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {outcome.genotype.join('')}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {outcome.percentage}%
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {outcome.phenotype}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </Stack>
          
          <Button
            variant="outlined"
            onClick={() => setBreedingResults(null)}
            sx={{ mt: 2 }}
          >
            Clear Results
          </Button>
        </Paper>
      )}
    </Stack>
  )
}

export default GeneticsTab